/**
 * API Client
 * Base fetch wrapper with automatic microservices routing
 * Matches the pattern used in the React Native app
 */

import { getLambdaUrlForAction } from '@/utils/apiRouter';
import { isMicroservicesEnabled } from '@/config/lambdaConfig';

export interface APIResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

/**
 * Get authentication token from localStorage
 */
const getAuthToken = (): string | null => {
  try {
    const authData = localStorage.getItem('authData');
    if (authData) {
      const parsed = JSON.parse(authData);
      return parsed.idToken || parsed.accessToken || null;
    }
  } catch (error) {
    console.error('Failed to get auth token:', error);
  }
  return null;
};

/**
 * Make API request with automatic routing to correct Lambda/microservice
 * Routes based on action type when microservices are enabled
 * Automatically includes JWT token in Authorization header
 */
export const apiRequest = async <T = unknown>(
  action: string,
  data: Record<string, unknown> = {}
): Promise<APIResponse<T>> => {
  try {
    // Get the correct URL based on action and microservices config
    const url = getLambdaUrlForAction(action);
    
    if (import.meta.env.DEV) {
      console.log(`[API] ${action} → ${isMicroservicesEnabled() ? 'microservice' : 'monolith'}`);
    }
    
    // Build headers
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    
    // Add Authorization header if token exists
    const token = getAuthToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        action,
        ...data,
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result as APIResponse<T>;
  } catch (error) {
    console.error('API request failed:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
};
